import * as GriddleModels from "../index";
import * as FormatMutation from "./FormatMutation";
import { ColumnType } from "../../../models";
import { mergeFormats } from "./FormatMutation";
// Shortening the following Util imports resulted in an issue when using
// grid-state-service in grid-views. Explicitly importing each for now.
import { DurationUtil } from "../../../util/DurationUtil";
import { PredecessorUtil } from "../../../util/PredecessorUtil";
import { SystemFormulaUtil } from "../../../util/SystemFormulaUtil";
import { StringUtil } from "../../../util/StringUtil";
export function toGriddleColumn(col) {
    return {
        description: col.description,
        format: FormatMutation.toGriddleColumnFormat(col.format),
        formula: col.formula,
        id: col.id,
        isHidden: col.hidden,
        isLocked: col.locked,
        isPrimary: col.primary,
        name: col.title,
        tags: col.tags,
        type: toGriddleColumnType(col.defaultType),
        validation: null,
        width: col.width
    };
}
export function toGriddleRow(row) {
    var hasAttachments = row.anchors.some(function (anchor) { return anchor.anchorType === "ATTACHMENT"; });
    var hasComments = row.anchors.some(function (anchor) { return anchor.anchorType === "DISCUSSION"; });
    return {
        displayNumber: 0,
        format: FormatMutation.toGriddleRowFormat(row.format),
        hasAttachments: hasAttachments,
        hasComments: hasComments,
        id: row.id,
        isExpanded: row.expanded,
        isLocked: row.locked,
        level: 0,
        onCriticalPath: undefined,
        onDrivingPath: undefined,
        onWorkingPath: undefined,
        userAudit: row.userAudit
    };
}
export function toGriddleRowInfo(rowInfo, nextRowInfo) {
    var isParent = false;
    if (nextRowInfo) {
        isParent = rowInfo.rowHierarchyLevel < nextRowInfo.rowHierarchyLevel;
    }
    return {
        rowId: rowInfo.rowId,
        rowIndex: rowInfo.rowOrderIndex + 1,
        rowLevel: rowInfo.rowHierarchyLevel,
        isParent: isParent
    };
}
export function getGriddleCellContents(cellDataType, value, formulaStr) {
    var dataType = cellDataType;
    var image = undefined;
    var text = value;
    var formula = undefined;
    var hyperlink;
    var containerLink;
    if (formulaStr) {
        if (SystemFormulaUtil.isCellImageFormula(formulaStr)) {
            var cellImage = SystemFormulaUtil.parseImageFormula(formulaStr);
            // check if cellImage returned an error
            if (cellImage.error) {
                var imageError = cellImage;
                dataType = GriddleModels.CellDataType.TEXT;
                text = imageError.error;
            }
            else {
                dataType = GriddleModels.CellDataType.IMAGE;
                image = cellImage;
                text = image.altText;
            }
        }
        else if (SystemFormulaUtil.isURLHyperlink(formulaStr)) {
            var formulaObj = SystemFormulaUtil.parseSystemFormulaString(formulaStr);
            if (formulaObj.error) {
                var formulaError = formulaObj;
                dataType = GriddleModels.CellDataType.TEXT;
                text = formulaError.error;
            }
            else {
                formulaObj = formulaObj;
                // convertURLFormulaToHTML uses toHTML, which ensures the resulting HTML is safe
                var safeHtmlString = SystemFormulaUtil.convertURLFormulaToHTML(formulaObj);
                dataType = GriddleModels.CellDataType.HYPERLINK;
                // url will always be available. we check the existence of a url in the above error block
                hyperlink = {
                    displayValue: value,
                    htmlValue: safeHtmlString,
                    url: formulaObj.formulaParms[1] || "" // url will always be available. we check the existence of a url in the above error block
                };
                formula = formulaStr;
            }
        }
        else if (SystemFormulaUtil.isContainerLinkFormula(formulaStr)) {
            var formulaObj = SystemFormulaUtil.parseSystemFormulaString(formulaStr);
            if (formulaObj.error) {
                var formulaError = formulaObj;
                dataType = GriddleModels.CellDataType.TEXT;
                text = formulaError.error;
            }
            else {
                formulaObj = formulaObj;
                dataType = GriddleModels.CellDataType.CONTAINERLINK;
                containerLink = {
                    containerId: Number(formulaObj.formulaParms[1])
                };
                text = value;
            }
        }
    }
    return {
        dataType: dataType,
        image: image,
        text: text,
        hyperlink: hyperlink,
        containerLink: containerLink,
        formula: formula
    };
}
export function toGriddleCell(gridServiceRow, gridServiceColumn, gridServiceCell, isRowEmpty) {
    var _a;
    var row = gridServiceRow;
    var column = gridServiceColumn;
    var baseCellFormat;
    var cell = gridServiceCell;
    if (!cell) {
        cell = {
            rowId: row.id,
            columnId: column.id,
            format: {
                format1: 0,
                format2: 0
            }
        };
        // determine baseFormat based on combined row and column (lowest priority) formatting
        baseCellFormat = mergeFormats(FormatMutation.toGriddleRowFormat(row.format), FormatMutation.toGriddleColumnFormat(column.format));
    }
    else {
        baseCellFormat = FormatMutation.toGriddleCellFormat(cell.format);
    }
    var conditionalFormat;
    if (cell.conditionalFormat && row.conditionalFormat) {
        var rowCF = FormatMutation.toGriddleCellFormat(row.conditionalFormat);
        var cellCF = FormatMutation.toGriddleCellFormat(cell.conditionalFormat);
        conditionalFormat = FormatMutation.mergeFormats(cellCF, rowCF);
    }
    else if (cell.conditionalFormat) {
        conditionalFormat = FormatMutation.toGriddleCellFormat(cell.conditionalFormat);
    }
    else if (row.conditionalFormat) {
        conditionalFormat = FormatMutation.toGriddleCellFormat(row.conditionalFormat);
    }
    // display format is the combination of conditional format + base format, with conditional formatting having higher priority
    var displayFormat = conditionalFormat ? FormatMutation.mergeFormats(conditionalFormat, baseCellFormat) : baseCellFormat;
    var griddleCell = {
        baseFormat: baseCellFormat,
        coord: { rowId: row.id, columnId: column.id },
        dataType: GriddleModels.CellDataType.NO_VALUE,
        displayFormat: displayFormat,
        formula: cell.formula,
        hasInboundLink: false,
        hasOutboundLinks: false,
        lastModifyDateTime: 0
    };
    switch ((_a = cell.cellValue) === null || _a === void 0 ? void 0 : _a.cellType) {
        // string values
        case "ARROWS3":
        case "ARROWS4":
        case "ARROWS5":
        case "DECISIONSHAPES":
        case "DECISIONSYMBOLS":
        case "DECISIONVCR":
        case "DIFFICULTY4":
        case "DIRECTIONS3":
        case "DIRECTIONS4":
        case "EFFORT6":
        case "HARVEY":
        case "HEART6":
        case "MONEY6":
        case "PAIN6":
        case "PROGRESS5":
        case "PRIORITY":
        case "PRIORITYHML":
        case "RYG":
        case "RYGB":
        case "RYGG":
        case "SIGNAL5":
        case "STAR6":
        case "WEATHER":
            if (typeof cell.cellValue.value === "string") {
                griddleCell.dataType = GriddleModels.CellDataType.TEXT;
                griddleCell.text = cell.cellValue.value;
            }
            break;
        case "PICKLISTTEXT":
        case "TEXT":
            if (typeof cell.cellValue.value === "string") {
                // check for cell formula
                var cellContents = getGriddleCellContents(GriddleModels.CellDataType.TEXT, cell.cellValue.value, cell.formula);
                Object.assign(griddleCell, cellContents);
            }
            break;
        case "SYS_USER":
            if (typeof cell.cellValue.name === "string") {
                griddleCell.dataType = GriddleModels.CellDataType.TEXT;
                griddleCell.text = cell.cellValue.name;
            }
            break;
        // number values
        case "NUMBER":
        case "PICKLISTNUMBER":
            if (typeof cell.cellValue.value === "number") {
                griddleCell.dataType = GriddleModels.CellDataType.NUMBER;
                griddleCell.number = cell.cellValue.value;
            }
            break;
        case "ABSTRACTDATETIME":
        case "DATE":
            if (typeof cell.cellValue.value === "string") {
                griddleCell.dataType = GriddleModels.CellDataType.DATE;
                griddleCell.date = cell.cellValue.value;
            }
            break;
        case "SYS_DATE":
            if (typeof cell.cellValue.value === "string") {
                griddleCell.dataType = GriddleModels.CellDataType.DATETIME;
                griddleCell.date = cell.cellValue.value;
            }
            break;
        // boolean values
        case "CHECKBOX":
            if (typeof cell.cellValue.value === "boolean") {
                if (isRowEmpty && !cell.cellValue.value) {
                    // row is empty and cell value is false - should result in an empty cell
                    break;
                }
                griddleCell.dataType = GriddleModels.CellDataType.BOOLEAN;
                griddleCell.boolean = cell.cellValue.value;
            }
            break;
        case "MULTIPICKLIST":
            if (cell.cellValue.values && cell.cellValue.values.length > 0) {
                // check for formula
                // we use values[0] since you can only upload one image per cell
                var cellContents = getGriddleCellContents(GriddleModels.CellDataType.MULTISELECT, cell.cellValue.values[0], cell.formula);
                if (cellContents.dataType === GriddleModels.CellDataType.MULTISELECT) {
                    griddleCell.multiSelect = toMultiSelectValues(cell.cellValue.values);
                }
                Object.assign(griddleCell, cellContents);
            }
            break;
        case "CONTACT":
            if (cell.cellValue.name) {
                griddleCell.dataType = GriddleModels.CellDataType.CONTACT;
                // TODO: how can we type guard this?
                griddleCell.contact = {
                    name: cell.cellValue.name,
                    emailAddress: undefined // TODO: implement
                };
            }
            break;
        case "MULTICONTACT":
            if (cell.cellValue.contacts && cell.cellValue.contacts.length > 0) {
                griddleCell.dataType = GriddleModels.CellDataType.MULTICONTACT;
                griddleCell.multiContact = cell.cellValue.contacts.map(function (contact) {
                    return {
                        name: contact.name,
                        emailAddress: undefined // TODO: implement
                    };
                });
            }
            break;
        case "DURATION":
            if (cell.cellValue.days !== undefined ||
                cell.cellValue.hours !== undefined ||
                cell.cellValue.minutes !== undefined ||
                cell.cellValue.seconds !== undefined ||
                cell.cellValue.milliseconds !== undefined ||
                cell.cellValue.weeks !== undefined ||
                cell.cellValue.elapsed !== undefined ||
                cell.cellValue.negative !== undefined) {
                griddleCell.dataType = GriddleModels.CellDataType.TEXT;
                griddleCell.duration = {
                    days: cell.cellValue.days,
                    hours: cell.cellValue.hours,
                    minutes: cell.cellValue.minutes,
                    seconds: cell.cellValue.seconds,
                    milliseconds: cell.cellValue.milliseconds,
                    weeks: cell.cellValue.weeks,
                    isElapsed: cell.cellValue.elapsed,
                    isNegative: cell.cellValue.negative
                };
                griddleCell.text = DurationUtil.convertDurationToString(griddleCell.duration);
            }
            break;
        case "PREDECESSOR":
            if (cell.cellValue.predecessorObject && cell.cellValue.predecessorObject.length > 0) {
                griddleCell.dataType = GriddleModels.CellDataType.TEXT;
                griddleCell.predecessors = cell.cellValue.predecessorObject.map(function (object) {
                    return {
                        rowId: object.rowId,
                        rowNumber: object.rowNumber,
                        lag: object.lag
                            ? {
                                days: object.lag.days,
                                hours: object.lag.hours,
                                minutes: object.lag.minutes,
                                seconds: object.lag.seconds,
                                milliseconds: object.lag.milliseconds,
                                weeks: object.lag.weeks,
                                isElapsed: object.lag.elapsed,
                                isNegative: object.lag.negative
                            }
                            : null,
                        dependencyType: object.type
                    };
                });
                griddleCell.text = griddleCell.predecessors.map(function (item) { return PredecessorUtil.convertPredecessorToString(item); }).join(", ");
            }
            break;
        default:
            // empty cells have no value and dataType of NO_VALUE
            // in checkbox/flag/star column, if row is not empty, create unchecked checkbox/flag/star
            if (column &&
                !isRowEmpty &&
                (column.defaultType == "CHECKBOX" || column.defaultType == "FLAG" || column.defaultType == "STAR")) {
                griddleCell.dataType = GriddleModels.CellDataType.BOOLEAN;
                griddleCell.boolean = false;
            }
            break;
    }
    return griddleCell;
}
/**
 * This method is used to take an array of strings of user input and convert them to multiselect values, which may contain links
 *
 * Parsing the links is expensive logic, thus the call to containsLink before attempting to parse
 *
 * @param inStrings - An array of strings (UNSAFE HTML) which contain one or more url links and are intended for presentation in the UI.
 * @return An array of multiselect values (with SAFE HTML) which have had any url links packaged as <a> tags.  Any unsafe HTML has been safely escaped.
 */
export function toMultiSelectValues(inStrings) {
    var multiSelectValues = [];
    inStrings.forEach(function (value) {
        if (StringUtil.containsLink(value)) {
            multiSelectValues.push({ hasHTML: true, safeHTML: StringUtil.toHtmlWithLinks(value), text: value });
        }
        else {
            multiSelectValues.push({ text: value });
        }
    });
    return multiSelectValues;
}
export function toGriddleColumnType(gridServiceColumn) {
    switch (gridServiceColumn) {
        case "ABSTRACTDATETIME":
            return ColumnType.COLUMNTYPE_ABSTRACTDATETIME;
        case "ARROWS3":
            return ColumnType.COLUMNTYPE_ARROWS3;
        case "ARROWS4":
            return ColumnType.COLUMNTYPE_ARROWS4;
        case "ARROWS5":
            return ColumnType.COLUMNTYPE_ARROWS5;
        case "CHECKBOX":
            return ColumnType.COLUMNTYPE_CHECKBOX;
        case "CONTACT":
            return ColumnType.COLUMNTYPE_CONTACT;
        case "DATE":
            return ColumnType.COLUMNTYPE_DATE;
        case "DECISIONSHAPES":
            return ColumnType.COLUMNTYPE_DECISIONSHAPES;
        case "DECISIONSYMBOLS":
            return ColumnType.COLUMNTYPE_DECISIONSYMBOLS;
        case "DECISIONVCR":
            return ColumnType.COLUMNTYPE_DECISIONVCR;
        case "DIFFICULTY4":
            return ColumnType.COLUMNTYPE_DIFFICULTY4;
        case "DIRECTIONS3":
            return ColumnType.COLUMNTYPE_DIRECTIONS3;
        case "DIRECTIONS4":
            return ColumnType.COLUMNTYPE_DIRECTIONS4;
        case "DURATION":
            return ColumnType.COLUMNTYPE_DURATION;
        case "EFFORT6":
            return ColumnType.COLUMNTYPE_EFFORT6;
        case "FLAG":
            return ColumnType.COLUMNTYPE_FLAG;
        case "HARVEY":
            return ColumnType.COLUMNTYPE_HARVEY;
        case "HEART6":
            return ColumnType.COLUMNTYPE_HEART6;
        case "MONEY6":
            return ColumnType.COLUMNTYPE_MONEY6;
        case "MULTICONTACT":
            return ColumnType.COLUMNTYPE_MULTICONTACT;
        case "MULTIPICKLIST":
            return ColumnType.COLUMNTYPE_MULTIPICKLIST;
        case "PAIN6":
            return ColumnType.COLUMNTYPE_PAIN6;
        case "PICKLIST":
            return ColumnType.COLUMNTYPE_PICKLIST;
        case "PREDECESSOR":
            return ColumnType.COLUMNTYPE_PREDECESSOR;
        case "PRIORITY":
            return ColumnType.COLUMNTYPE_PRIORITY;
        case "PRIORITYHML":
            return ColumnType.COLUMNTYPE_PRIORITYHML;
        case "PROGRESS5":
            return ColumnType.COLUMNTYPE_PROGRESS5;
        case "RYG":
            return ColumnType.COLUMNTYPE_RYG;
        case "RYGB":
            return ColumnType.COLUMNTYPE_RYGB;
        case "RYGG":
            return ColumnType.COLUMNTYPE_RYGG;
        case "SIGNAL5":
            return ColumnType.COLUMNTYPE_SIGNAL5;
        case "STAR":
            return ColumnType.COLUMNTYPE_STAR;
        case "STAR6":
            return ColumnType.COLUMNTYPE_STAR6;
        case "SYS_INSERTBY":
            return ColumnType.COLUMNTYPE_SYS_INSERTBY;
        case "SYS_INSERTDATE":
            return ColumnType.COLUMNTYPE_SYS_INSERTDATE;
        case "SYS_MODIFYBY":
            return ColumnType.COLUMNTYPE_SYS_MODIFYBY;
        case "SYS_MODIFYDATE":
            return ColumnType.COLUMNTYPE_SYS_MODIFYDATE;
        case "SYS_SHEETNAME":
            return ColumnType.COLUMNTYPE_SYS_SHEETNAME;
        case "SYS_UNIQUEID":
            return ColumnType.COLUMNTYPE_SYS_UNIQUEID;
        case "WEATHER":
            return ColumnType.COLUMNTYPE_WEATHER;
        case "TEXTNUMBER":
        default:
            return ColumnType.COLUMNTYPE_TEXTNUMBER;
    }
}
export function isGridServiceCellEmpty(cell) {
    var _a;
    switch ((_a = cell.cellValue) === null || _a === void 0 ? void 0 : _a.cellType) {
        case "ABSTRACTDATETIME":
        case "ARROWS3":
        case "ARROWS4":
        case "ARROWS5":
        case "DATE":
        case "DECISIONSHAPES":
        case "DECISIONSYMBOLS":
        case "DECISIONVCR":
        case "DIFFICULTY4":
        case "DIRECTIONS3":
        case "DIRECTIONS4":
        case "EFFORT6":
        case "HARVEY":
        case "HEART6":
        case "MONEY6":
        case "NUMBER":
        case "PAIN6":
        case "PICKLISTNUMBER":
        case "PICKLISTTEXT":
        case "PROGRESS5":
        case "PRIORITY":
        case "PRIORITYHML":
        case "RYG":
        case "RYGB":
        case "RYGG":
        case "SIGNAL5":
        case "STAR6":
        case "SYS_DATE":
        case "TEXT":
        case "WEATHER":
            if (cell.cellValue.value) {
                return false;
            }
            break;
        case "CONTACT":
        case "SYS_USER":
            if (cell.cellValue.name) {
                return false;
            }
            break;
        case "CHECKBOX":
        case "FLAG":
        case "STAR":
            if (cell.cellValue.value) {
                // if boolean value is true, it's not empty
                return false;
            }
            break;
        case "MULTIPICKLIST":
            if (cell.cellValue.values && cell.cellValue.values.length > 0) {
                return false;
            }
            break;
        case "MULTICONTACT":
            if (cell.cellValue.contacts && cell.cellValue.contacts.length > 0) {
                return false;
            }
            break;
        case "DURATION":
            if (cell.cellValue.days ||
                cell.cellValue.hours ||
                cell.cellValue.minutes ||
                cell.cellValue.seconds ||
                cell.cellValue.milliseconds ||
                cell.cellValue.weeks ||
                cell.cellValue.elapsed) {
                return false;
            }
            break;
        case "PREDECESSOR":
            if (cell.cellValue.predecessorObject && cell.cellValue.predecessorObject.length > 0) {
                return false;
            }
            break;
        default:
            // empty cells have no value and dataType of NO_VALUE
            break;
    }
    return true;
}
export function isGridServiceRowEmpty(row) {
    if (row.cells == undefined || row.cells.length < 1) {
        return true;
    }
    for (var _i = 0, _a = row.cells; _i < _a.length; _i++) {
        var cell = _a[_i];
        if (!isGridServiceCellEmpty(cell)) {
            return false;
        }
    }
    return true;
}
//# sourceMappingURL=ModelMutations.js.map